import React from 'react';
import i18next from 'i18next';
import {
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getFacetedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import type { ColumnDef, TableOptions } from '@tanstack/react-table';
import { Button, Input } from 'reactstrap';
import { SimpleTableFilter } from './SimpleTableFilter';


const availablePageSizes = [5, 10, 25, 50, 100] as const;

interface SimpleTableWithSortingProps {
  addWrapper?: boolean;
  className?: string;
  columns: ColumnDef<unknown, unknown>[];
  data: any[];
  minPagesAmountToShowInput?: number;
  hasFilters?: boolean;
  pageSize?: (typeof availablePageSizes)[number];
  showFooter?: boolean;
}

const adminStatsTableStyle = {
  thStyle: {
    borderBottom: 'solid 3px red',
    background: 'aliceblue',
    color: 'black',
    cursor: 'pointer',
    fontWeight: 'bold',
    verticalAlign: 'middle',
    textTransform: 'none',
  } as React.CSSProperties,
  tdStyle: {
    padding: '10px',
    border: 'solid 1px gray',
  } as React.CSSProperties,
};


export const SimpleTableWithSorting = (props: SimpleTableWithSortingProps) => {
  const { minPagesAmountToShowInput = 4 } = props;
  const data = React.useMemo(() => props.data, [props.data]);
  const columns = React.useMemo(() => props.columns, [props.columns]);

  const options = React.useMemo<TableOptions<any>>(() => {
    const paginationState = props.pageSize ? { pageSize: props.pageSize } : {};
    
    const options: TableOptions<any> = {
      columns,
      data,
      initialState: { pagination: paginationState },
      getCoreRowModel: getCoreRowModel(),
      getPaginationRowModel: getPaginationRowModel(),
      getSortedRowModel: getSortedRowModel(),
      getFilteredRowModel: getFilteredRowModel(),
      getFacetedRowModel: getFacetedRowModel(),
      enableColumnFilters: props.hasFilters ?? false,
      enableFilters: props.hasFilters ?? false,
    };
    return options;
  }, [
    data,
    columns,
    props.pageSize,
    props.hasFilters,
  ]);

  const tableInstance = useReactTable(options);
  const {
    getState,
    getHeaderGroups,
    getRowModel,
    setPageIndex,
    previousPage,
    nextPage,
    setPageSize,
    getCanPreviousPage,
    getCanNextPage,
    getPageCount,
  } = tableInstance;
  
  const { pagination } = getState();
  const { pageIndex, pageSize } = pagination;
  const pageCount = getPageCount();

  const canPreviousPage = getCanPreviousPage();
  const canNextPage = getCanNextPage();

  const RootTableElement = props.addWrapper ? 'div' : React.Fragment;
  const rootTableElementProps = props.addWrapper
    ? { className: 'table-rwd-wrapper ' }
    : {};

  const { showFooter = true } = props;

  return (
    <RootTableElement {...rootTableElementProps}>
      <table
        // {...getTableProps()} @TODO
        className={`table ${props.className}`}
      >
        <thead>
          {getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <th
                  key={header.id}
                  style={adminStatsTableStyle.thStyle}
                  onClick={header.column.getToggleSortingHandler()}
                >
                  <div>
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                    <span>
                      {header.column.getIsSorted()
                        ? header.column.getIsSorted() === 'desc'
                          ? ' 🔽'
                          : ' 🔼'
                        : ''}
                    </span>
                  </div>
                  <div>
                    {header.column.getCanFilter() ? (
                      <div>
                        <SimpleTableFilter column={header.column} />
                      </div>
                    ) : null}
                  </div>
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {getRowModel().rows.map((row) => {
            return (
              <tr key={row.id}>
                {row.getVisibleCells().map((cell) => {
                  return (
                    <td
                      key={cell.id}
                      style={adminStatsTableStyle.tdStyle}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
      {showFooter && (
        <div className="d-flex flex-row justify-content-between align-items-center mb-3">
          <div className="d-flex align-items-center">
            <Button
              type="button"
              onClick={() => previousPage()}
              disabled={!canPreviousPage}
            >
              &lt; {i18next.t('simple-table.previous')}
            </Button>
            <div className="mx-2">
              <em>
                {i18next.t('simple-table.page-of', { current: pageIndex + 1, total: pageCount })}
              </em>
            </div>
            <Button
              type="button"
              className="mr-2"
              onClick={nextPage}
              disabled={!canNextPage}
            >
              {i18next.t('simple-table.next')} &gt;
            </Button>
          </div>
          {!minPagesAmountToShowInput ||
          pageCount >= minPagesAmountToShowInput ? (
            <div className="d-flex align-items-center">
              <div>{i18next.t('simple-table.go-to-page')}</div>
              <Input
                type="number"
                className="mw-150 ml-2 mr-3"
                defaultValue={pageIndex + 1 || 1}
                onChange={(e) => {
                  const page = e.target.value ? Number(e.target.value) - 1 : 0;
                  setPageIndex(page);
                }}
              />
            </div>
          ) : null}
          <Input
            type="select"
            className="mw-200"
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
            }}
          >
            {availablePageSizes.map((pageSize) => (
              <option key={pageSize} value={pageSize}>
                {i18next.t('simple-table.page-size', { size: pageSize })}
              </option>
            ))}
          </Input>
        </div>
      )}
    </RootTableElement>
  );
};

SimpleTableWithSorting.defaultProps = {
  addWrapper: true,
  className: '',
};

export default SimpleTableWithSorting;
